// src/features/AudioRecorder.jsx
import React, { useState, useRef } from 'react';
import { LiveAudioVisualizer } from 'react-audio-visualize';
import { MicrophoneIcon } from '@heroicons/react/20/solid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Tooltip from '../components/SharedComponents/ToolTip';
import axios from 'axios';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

const pickMime = () => {
  // Chrome/Edge/Firefox
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  // Safari (iOS/macOS) prefers MP4/AAC
  if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) return 'audio/mp4;codecs=mp4a.40.2';
  if (MediaRecorder.isTypeSupported('audio/aac')) return 'audio/aac';
  return ''; // let browser choose
};

function extForMime(mime) {
  if (!mime) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'm4a'; // common extension for audio/mp4
  if (mime.includes('aac')) return 'aac';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

async function getAudioDuration(file) {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    audio.preload = "metadata";

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);
      resolve(audio.duration); // <-- duration in seconds
    };

    audio.onerror = (e) => reject(e);

    audio.src = URL.createObjectURL(file);
  });
}



const AudioRecorder = ({ onInputUpdate }) => {
  const [recording, setRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const fullTranscriptRef = useRef('');
  const toastIdRef = useRef(null);
  const uploadingRef = useRef(false);          // throttle uploads
  const abortCtrlRef = useRef(null);           // cancel inflight on stop
  const lastChunkTimeRef = useRef(0);

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);



      const selectedMime = pickMime();                 // <- use helper
      const mr = new MediaRecorder(stream, { mimeType: selectedMime });
      const chunks = [];
      let recordedMime = '';                           // determined from first chunk

      mr.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          recordedMime = recordedMime || event.data.type || selectedMime || '';
          lastChunkTimeRef.current = Date.now();
        }
      };

      mr.onstop = async () => {
        try {
          // Build Blob with whatever the recorder actually produced
          const blob = new Blob(chunks, { type: recordedMime || undefined });
          const ext = extForMime(recordedMime || selectedMime);
          const file = new File([blob], `recording-${Date.now()}.${ext}`, {
            type: recordedMime || selectedMime || undefined,
          });

          if (file.size < 1024) throw new Error('Empty/too-small recording (size < 1KB).');

          const fd = new FormData();
          fd.append('file', file, file.name);
          fd.append('mime', file.type || '');

          // Optional: inspect payload
          // for (const [k, v] of fd.entries()) {
          //   console.log(k, v instanceof File ? { name: v.name, type: v.type, size: v.size } : v);
          // }

          const resp = await axios.post(`${EndPoint}/careplan/transcribe`, fd, {
            withCredentials: true,
            // DO NOT set Content-Type manually; browser will add multipart boundary
            responseType: 'json',
          });

          onInputUpdate?.(resp.data);

          toast.update(toastIdRef.current, {
            render: 'Recording uploaded successfully',
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 1500,
          });
          toastIdRef.current = null;
        } catch (err) {
          console.error('Upload error:', err);
          toast.update(toastIdRef.current, {
            render: `Upload failed${err.response?.status ? ` (${err.response.status})` : ''}`,
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 2000,
          });
        }
      };

      mr.start(); // keep recording until stopRecording() is called
      setMediaRecorder(mr);
      setRecording(true);
    } catch (err) {
      console.error(err);
      toast.error('Microphone access failed.');
      stopRecording(true);
    }
  };

  const stopRecording = (fromError = false) => {
    setRecording(false);

    // Flush final data before stopping, if possible
    try {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        if (Date.now() - lastChunkTimeRef.current > 500) {
          mediaRecorder.requestData();
        }
        mediaRecorder.stop();
      }
    } catch (_) {}

    // Cancel any inflight upload
    if (abortCtrlRef.current) {
      try { abortCtrlRef.current.abort(); } catch (_) {}
      abortCtrlRef.current = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
      setMediaStream(null);
    }

    if (!fromError && toastIdRef.current) {
      toast.update(toastIdRef.current, {
        render: 'Recording stopped',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 1100,
      });
      toastIdRef.current = null;
    }

    setMediaRecorder(null);
  };


  const deidentifyText = (input) =>
    (input || '')
      .replace(/\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, '[Name]')
      .replace(/\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g, '[Date]')
      .replace(/\b\d{4,5}\s?[A-Za-z]+\s[A-Za-z]+\b/g, '[Address]')
      .replace(/\b\d{10,12}\b/g, '[Phone/ID]')
      .replace(/\b(?:Mr|Mrs|Ms|Dr)\.?\s[A-Z][a-z]+\b/g, '[Title Name]');

  return (
    <div className="flex flex-col items-center my-4 h-auto">
      <div className="flex items-center space-x-4 sm:ml-[700px]">
        <button
          id="microphone"
          className={`${
            recording ? 'bg-red-600 hover:bg-red-700' : 'bg-sky-800 hover:bg-sky-900'
          } flex items-center text-white px-2 py-2 rounded-3xl hover:shadow-xl relative`}
          onClick={toggleRecording}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <MicrophoneIcon className="w-8 h-8" />
          {recording && <p className="px-2">Recording</p>}
        </button>

        {showTooltip && (
          <div className="absolute ml-80 -mt-20">
            <Tooltip text="Voice recording" />
          </div>
        )}

        <div style={{ width: 300, height: 50 }}>
          {recording && mediaRecorder ? (
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorder}
              width={300}
              height={50}
              barWidth={3}
              gap={2}
              barColor={'#205680'}
            />
          ) : null}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AudioRecorder;
